/**
 * TripReplayMap — GPS replay of a ride, opened from the grid's Replay action.
 * src/components/map/TripReplayMap.jsx
 *
 *   import TripReplayMap from '../map/TripReplayMap';
 *   <TripReplayMap ride={row} onClose={() => setReplayRow(null)} />
 *
 * Props
 *   ride       Ride grid row (AirlineCode, TransactionId, RideId, VehicleId, VehicleNo,
 *              DriverName, PaxName, TransporterName, milestone times …). camelCase or PascalCase.
 *   onClose    optional — shows the × in the title bar.
 *   endpoint   replay API path, called with axiosGet (default below).
 *   params     optional override of the query params sent to the endpoint.
 *   replay     optional VehicleReplayModel you already fetched — skips the request.
 *   height     container height (default '100%'); give the modal body a height.
 *
 * Data shaping lives in tripReplayData.js, marker artwork in tripReplayMarkers.js and the
 * side panel in TripReplayInfo.jsx. Playback drives Leaflet imperatively through refs; React
 * only re-renders the clock/readout, throttled.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './TripReplayMap.css';
import { axiosGet } from '../../apis/useAxios';
import {
  AMBER, GREEN, RED, buildData, colorAt, colourRuns, hav, headingAt, hms, indexAt, pick, statusFor,
} from './tripReplayData';
import { END_HTML, START_HTML, statusPinHtml, stopHtml, vehicleHtml } from './tripReplayMarkers';
import TripReplayInfo from './TripReplayInfo';
import { createReplaySound, cueIndexAfter } from './tripReplaySound';

const DEFAULT_ENDPOINT = '/Tracking/replay';
const TILE = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const RATES = [10, 30, 60, 120];
const PREFS_KEY = 'trip-replay-display-v1';
const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
const UI_REFRESH_MS = 100;    // React clock/readout refresh while playing; the map itself updates every frame
const FOLLOW_MARGIN = 0.2;    // Follow re-centres only once the vehicle leaves the middle 60% of the map
// Route not yet driven: dark casing + soft glow + bright round dots (a ~0 dash with round caps = a dot every 10 px)
const ROUTE_AHEAD = [
  { color: '#0c1014', weight: 8, opacity: 0.55, lineCap: 'round', lineJoin: 'round', interactive: false },
  { color: '#ffffff', weight: 6, opacity: 0.12, lineCap: 'round', lineJoin: 'round', interactive: false },
  { color: '#eafff6', weight: 4, opacity: 0.95, dashArray: '0.1 10', lineCap: 'round', interactive: false },
];
const EMPTY_LAYERS ={ trail: null, runs: [], runAt: [], played: 0, live: null, liveColor: '', pins: null, vehicle: null, iconKey: '', rotEl: null, spdEl: null };

function loadPrefs() {
  const defaults = { markerStyle: 'car', trailMode: 'speed', showStops: true, showStatus: true, soundOn: true };
  try {
    const p = { ...defaults, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') };
    return p.trailMode === 'solid' ? p : { ...p, trailMode: 'speed' };   // drops retired modes (e.g. 'status')
  } catch (e) { return defaults; }
}

/** Shows the trail up to fix i: each colour run's polyline holds only its already-played part. */
function setPlayed(lay, f, i) {
  const lo = Math.min(lay.played, i), hi = Math.max(lay.played, i);
  const last = lay.runAt.length - 1;
  for (let r = lay.runAt[Math.min(lo, last)]; r <= lay.runAt[Math.min(hi, last)]; r++) {
    const run = lay.runs[r];
    const upto = Math.min(i, run.to);
    run.line.setLatLngs(upto > run.from ? f.slice(run.from, upto + 1).map(x => x.ll) : []);
  }
  lay.played = i;
}


/* ======================================================================== */
export default function TripReplayMap({
  ride = {}, onClose, endpoint = DEFAULT_ENDPOINT, params, replay, height = '100%', className,
}) {
  const hostRef = useRef(null);
  const trackRef = useRef(null);
  const mapRef = useRef(null);
  const layers = useRef({ ...EMPTY_LAYERS });
  const idxRef = useRef(0);
  const headingRef = useRef(0);
  const tRef = useRef(0);
  const curRef = useRef({ speed: 0, fix: null, covered: 0 });
  const playingRef = useRef(false);
  const rateRef = useRef(60);
  const followRef = useRef(false);
  const rafRef = useRef(0);
  const ctlRef = useRef({});
  const jumpRef = useRef(() => {});   // status pins (created in an effect) call the latest jumpTo
  const soundRef = useRef(null);      // Web Audio engine + chimes, created on the first click that needs it
  const soundOnRef = useRef(true);
  const cuesRef = useRef([]);
  const cueIdxRef = useRef(0);
  const lastIdxRef = useRef(0);

  const [model, setModel] = useState(replay || null);
  const [loading, setLoading] = useState(!replay);
  const [error, setError] = useState('');
  const [T, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(60);
  const [follow, setFollow] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);
  const [wide, setWide] = useState(true);
  const [showStatus, setShowStatus] = useState(prefs.showStatus);   // per trip: saved choice, but off for manually-completed trips

  useEffect(() => { rateRef.current = rate; }, [rate]);
  useEffect(() => { followRef.current = follow; }, [follow]);
  const updatePrefs = (patch) => setPrefs(p => {
    const next = { ...p, ...patch };
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(next)); } catch (e) { /* storage unavailable */ }
    return next;
  });

  /* ---------- fetch ---------- */
  // Keyed on the ride id, not the row object, so a parent re-render doesn't refetch and rebuild.
  const rideId = pick(ride, 'rideId');
  const query = useMemo(() => params || { tripId: rideId }, [params, rideId]);

  useEffect(() => {
    if (replay) { setModel(replay); setLoading(false); return undefined; }
    let alive = true;
    setLoading(true); setError('');
    axiosGet(endpoint, query)
      .then(res => { if (alive) setModel(res && res.payload !== undefined ? res.payload : res); })
      .catch(err => { if (alive) setError((err && err.message) || 'Could not load replay'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [replay, endpoint, query]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const data = useMemo(() => (model ? buildData(model, ride) : null), [model, rideId]);
  const plate = pick(ride, 'vehicleNo') || '—';

  /* ---------- map ---------- */
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    const own = document.createElement('div');       // private container: safe under StrictMode remounts
    own.className = 'ltr-map';
    host.appendChild(own);
    // Canvas renderer: one bitmap for all trail polylines instead of an SVG path each.
    const m = L.map(own, { zoomControl: false, attributionControl: true, preferCanvas: true, center: [19.05, 72.85], zoom: 12 });
    L.tileLayer(TILE, { maxZoom: 19, attribution: '© OpenStreetMap contributors' }).addTo(m);
    m.attributionControl.setPrefix(false);
    m.on('dragstart', () => setFollow(false));
    mapRef.current = m;
    const ro = new ResizeObserver(() => { m.invalidateSize(); setWide(host.clientWidth >= 640); });
    ro.observe(host);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(rafRef.current);
      if (soundRef.current) { soundRef.current.dispose(); soundRef.current = null; }
      m.remove();
      own.remove();
      mapRef.current = null;
      layers.current = { ...EMPTY_LAYERS };
    };
  }, []);

  const fitAll = useCallback(() => {
    const m = mapRef.current;
    if (!m || !data) return;
    setFollow(false);
    const w = hostRef.current ? hostRef.current.clientWidth : 1000;
    const b = L.latLngBounds(data.fixes.map(x => x.ll));
    if (!b.isValid()) return;
    m.fitBounds(b, {
      paddingTopLeft: [w >= 640 ? 290 : 20, 24], paddingBottomRight: [w >= 640 ? 220 : 50, 130], animate: false,
    });
  }, [data]);

  /* position at time t → marker, heading, trail */
  const applyT = useCallback((t) => {
    const m = mapRef.current, lay = layers.current;
    if (!m || !data || !lay.vehicle || !lay.trail) return;
    const f = data.fixes;
    t = Math.max(0, Math.min(data.end, t));
    const i = indexAt(f, t, idxRef.current);
    idxRef.current = i;
    const a = f[i], b = f[i + 1];
    const k = b.t > a.t ? Math.max(0, Math.min(1, (t - a.t) / (b.t - a.t))) : 0;
    const ll = [a.ll[0] + (b.ll[0] - a.ll[0]) * k, a.ll[1] + (b.ll[1] - a.ll[1]) * k];
    const speed = a.speed + (b.speed - a.speed) * k;
    if (speed >= 1) headingRef.current = headingAt(data.seg, i, k);   // at a standstill: hold the last heading

    if (i !== lay.played) setPlayed(lay, f, i);
    lay.live.setLatLngs([a.ll, ll]);
    const liveColor = prefs.trailMode === 'solid' ? GREEN : colorAt(data.codes, a.speed);
    if (liveColor !== lay.liveColor) { lay.liveColor = liveColor; lay.live.setStyle({ color: liveColor }); }

    const [, color] = statusFor(a, speed);
    const key = prefs.markerStyle + color + (speed >= 1);
    if (key !== lay.iconKey) {
      lay.iconKey = key;
      lay.vehicle.setIcon(L.divIcon({ className: '', html: vehicleHtml(prefs.markerStyle, color, speed >= 1, plate), iconSize: [44, 44], iconAnchor: [22, 22] }));
      const el = lay.vehicle.getElement();
      lay.rotEl = el ? el.querySelector('[data-rot]') : null;
      lay.spdEl = el ? el.querySelector('[data-spd]') : null;
    }
    lay.vehicle.setLatLng(ll);
    if (lay.rotEl) lay.rotEl.style.transform = `rotate(${headingRef.current}deg)`;
    if (lay.spdEl) lay.spdEl.textContent = Math.round(speed);
    curRef.current = { speed, fix: a, covered: data.dist[i] + hav(a.ll, ll) };

    if (followRef.current && playingRef.current) {
      const p = m.latLngToContainerPoint(ll), size = m.getSize();
      const out = p.x < size.x * FOLLOW_MARGIN || p.x > size.x * (1 - FOLLOW_MARGIN)
        || p.y < size.y * FOLLOW_MARGIN || p.y > size.y * (1 - FOLLOW_MARGIN);
      if (out) m.panTo(ll, { animate: false });
    }
    tRef.current = t;
  }, [data, prefs.trailMode, prefs.markerStyle, plate]);
  const applyRef = useRef(applyT);
  applyRef.current = applyT;

  /* trail — background route + one polyline per colour run, rebuilt only for new data / trail mode */
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !data) return undefined;
    const f = data.fixes, all = f.map(x => x.ll);
    const g = L.layerGroup().addTo(m);
    ROUTE_AHEAD.forEach(style => L.polyline(all, style).addTo(g));   // dotted route; the played trail draws over it
    const runs = (prefs.trailMode === 'solid' ? colourRuns(f, () => GREEN) : data.runs).map(r => ({
      ...r, line: L.polyline([], { color: r.color, weight: 5, opacity: 0.95, lineCap: 'round', lineJoin: 'round', interactive: false }).addTo(g),
    }));
    const runAt = new Array(f.length);
    runs.forEach((r, n) => { for (let i = r.from; i <= r.to; i++) if (runAt[i] == null) runAt[i] = n; });
    const live = L.polyline([f[0].ll, f[0].ll], { color: GREEN, weight: 5, opacity: 0.95, lineCap: 'round', interactive: false }).addTo(g);
    Object.assign(layers.current, { trail: g, runs, runAt, played: 0, live, liveColor: '' });
    applyRef.current(tRef.current);
    return () => { m.removeLayer(g); layers.current.trail = null; };
  }, [data, prefs.trailMode]);

  /* start / end / stop / milestone pins */
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !data) return undefined;
    const f = data.fixes;
    const g = L.layerGroup().addTo(m);
    const pin = (html, w, h, ax, ay, ll, z = 0) => L.marker(ll, {
      interactive: false, zIndexOffset: z, icon: L.divIcon({ className: '', html, iconSize: [w, h], iconAnchor: [ax, ay] }),
    }).addTo(g);
    pin(START_HTML, 18, 18, 9, 9, f[0].ll);
    pin(END_HTML, 30, 38, 15, 36, f[f.length - 1].ll, 200);
    if (prefs.showStops) data.stops.forEach(s => pin(stopHtml(s.label), 0, 0, 10, 9, s.ll, 100));
    // trip-status pins (status + time): numbered, stacked when several share a spot, click to jump there
    if (showStatus) {
      data.events.forEach(ev => L.marker(ev.ll, {
        zIndexOffset: 300 + ev.n,
        icon: L.divIcon({ className: '', html: statusPinHtml(ev), iconSize: [26, 26], iconAnchor: [13, 13 + ev.stack * 30] }),
      }).on('click', () => jumpRef.current(ev)).addTo(g));
    }
    return () => m.removeLayer(g);
  }, [data, prefs.showStops, showStatus]);

  /* vehicle marker */
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !data) return undefined;
    const vehicle = L.marker(data.fixes[0].ll, { interactive: false, zIndexOffset: 1000, icon: L.divIcon({ className: '', html: '', iconSize: [44, 44], iconAnchor: [22, 22] }) }).addTo(m);
    Object.assign(layers.current, { vehicle, iconKey: '', rotEl: null, spdEl: null });
    applyRef.current(tRef.current);
    return () => { m.removeLayer(vehicle); layers.current.vehicle = null; };
  }, [data]);

  /* vehicle style changed → just swap the icon */
  useEffect(() => { applyRef.current(tRef.current); }, [prefs.markerStyle]);

  /* status pins start hidden when the API flags the trip as not really driven (manualHint) */
  useEffect(() => {
    if (data) setShowStatus(data.manualHint ? false : prefs.showStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const toggleStatus = () => {
    const next = !showStatus;
    setShowStatus(next);
    if (!data || !data.manualHint) updatePrefs({ showStatus: next });   // a one-off look at a flagged trip isn't saved
  };

  /* new data → start from the beginning, framed */
  useEffect(() => {
    if (!data) return;
    pause();
    tRef.current = 0; idxRef.current = 0; lastIdxRef.current = 0;
    headingRef.current = data.seg[0] || 0;   // face the first direction of travel, not north
    applyRef.current(0);
    setT(0);
    requestAnimationFrame(() => { if (mapRef.current) { mapRef.current.invalidateSize(); fitAll(); } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /* ---------- playback ---------- */
  const tick = useCallback(function loop(now) {
    if (!playingRef.current || !data) return;
    const last = loop.last || now;
    const dt = Math.min(0.1, (now - last) / 1000);
    loop.last = now;
    const prevT = tRef.current;
    const t = Math.min(data.end, prevT + dt * rateRef.current);
    applyRef.current(t);
    playSound(prevT, t);
    if (t >= data.end) { setT(data.end); pause(); return; }
    if (!loop.ui || now - loop.ui > UI_REFRESH_MS) { loop.ui = now; setT(t); }
    rafRef.current = requestAnimationFrame(loop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  /* sound for the playback step prevT → t: engine follows speed; chimes for cues passed and new over-speed */
  function playSound(prevT, t) {
    const snd = soundRef.current;
    if (!soundOnRef.current || !snd) return;
    snd.setEngine(curRef.current.speed, true);
    const cues = cuesRef.current;
    let c = cueIdxRef.current;
    while (c < cues.length && cues[c].t <= t) { if (cues[c].t > prevT) snd.cue(cues[c].kind); c++; }
    cueIdxRef.current = c;
    const i = idxRef.current, f = data.fixes;
    if (i > lastIdxRef.current && f[i].over && !f[i - 1].over) snd.cue('over');
    lastIdxRef.current = i;
  }
  const ensureSound = () => {
    if (!soundRef.current) soundRef.current = createReplaySound();
    return soundRef.current;
  };

  const seek = useCallback((t) => {
    if (!data) return;
    applyRef.current(t);
    setT(tRef.current);
    cueIdxRef.current = cueIndexAfter(cuesRef.current, tRef.current);   // jumping never fires chimes
    lastIdxRef.current = idxRef.current;
  }, [data]);
  function play() {
    if (!data || playingRef.current) return;
    if (soundOnRef.current) ensureSound();   // play click is the user gesture audio needs
    if (tRef.current >= data.end) seek(0);
    playingRef.current = true;
    tick.last = 0; tick.ui = 0;
    setPlaying(true);
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  }
  function pause() {
    playingRef.current = false;
    cancelAnimationFrame(rafRef.current);
    if (soundRef.current) soundRef.current.stop();
    setPlaying(false);
    setT(tRef.current);
  }
  const toggle = () => (playingRef.current ? pause() : play());
  ctlRef.current = { toggle, seek };

  useEffect(() => {
    const onKey = (e) => {
      if (/input|textarea|select/i.test(e.target.tagName)) return;
      if (e.code === 'Space') { e.preventDefault(); ctlRef.current.toggle(); }
      if (e.key === 'ArrowRight') ctlRef.current.seek(tRef.current + 30);
      if (e.key === 'ArrowLeft') ctlRef.current.seek(tRef.current - 30);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const onScrubDown = (e) => {
    const el = trackRef.current;
    if (!el || !data) return;
    const r = el.getBoundingClientRect();
    let x = e.clientX, raf = 0;
    const go = () => { raf = 0; seek(Math.max(0, Math.min(1, (x - r.left) / r.width)) * data.end); };
    go();
    const mv = (ev) => { x = ev.clientX; if (!raf) raf = requestAnimationFrame(go); };   // at most one seek per frame
    const up = () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', mv);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', mv);
    window.addEventListener('pointerup', up);
  };

  const jumpTo = useCallback((item) => {
    seek(item.start);
    setFollow(false);
    if (mapRef.current) mapRef.current.setView(item.ll, 16, { animate: true });
  }, [seek]);
  jumpRef.current = jumpTo;

  /* ---------- view values (static ones memoized — this renders ~10×/s while playing) ---------- */
  const cues = useMemo(() => (data ? [
    ...data.events.map(x => ({ t: x.start, kind: 'milestone' })),
    ...(prefs.showStops ? data.stops.map(x => ({ t: x.start, kind: 'stop' })) : []),
  ].sort((a, b) => a.t - b.t) : []), [data, prefs.showStops]);
  useEffect(() => {
    cuesRef.current = cues;
    cueIdxRef.current = cueIndexAfter(cues, tRef.current);
  }, [cues]);
  useEffect(() => {
    soundOnRef.current = prefs.soundOn;
    if (!prefs.soundOn && soundRef.current) soundRef.current.stop();
  }, [prefs.soundOn]);
  const toggleSound = () => {
    if (!prefs.soundOn) ensureSound();
    updatePrefs({ soundOn: !prefs.soundOn });
  };

  const timeline = useMemo(() => (data ? [
    ...(prefs.showStops ? data.stops.map(x => ({ ...x, kind: 'stop', color: RED, sub: x.sub || 'Stoppage' })) : []),
    ...data.events.map(x => ({ ...x, label: `${x.n} · ${x.label}`, sub: x.sub || 'Trip status' })),
  ].sort((a, b) => a.start - b.start) : []), [data, prefs.showStops]);
  const legend = useMemo(() => (data && data.codes.length
    ? data.codes.map(c => ({ color: c.color, label: c.min === c.max ? `${c.name} ${c.min}` : `${c.name} ${c.min}–${c.max}` }))
    : [{ color: RED, label: 'Stopped' }, { color: AMBER, label: 'Slow < 25 km/h' }, { color: GREEN, label: 'Moving' }]), [data]);
  const stopTicks = useMemo(() => (data && prefs.showStops ? data.stops.map(st => (
    <div key={st.start} className="ltr-tick" style={{ left: `${st.start / data.end * 100}%`, width: `${st.len / data.end * 100}%` }} />
  )) : null), [data, prefs.showStops]);
  // unique: the activity name is often the flight number again (e.g. "QP 1261"), and chips are keyed by text
  const chips = [...new Set([
    pick(ride, 'flightNumber'),
    [pick(ride, 'airportCode'), pick(ride, 'flightTerminal')].filter(Boolean).join(' '),
    pick(ride, 'activityDisplay') || pick(ride, 'activityType'),
  ].filter(Boolean).map(c => String(c).trim()))];

  const s = data ? data.summary : null;
  const end = data ? data.end : 1;
  const cur = curRef.current;
  const [statusLabel, statusColor] = statusFor(cur.fix, cur.speed || 0);
  const heading = Math.round(headingRef.current);
  const clock = data ? hms(data.t0 + T) : '--:--:--';
  const pct = `${(T / end * 100).toFixed(2)}%`;

  return (
    <div className={`ltr ${className || ''}`} style={{ height }}>
      <header className="ltr-head">
        <span className="ltr-title">Trip Replay · <span className="ltr-mono">{plate}</span></span>
        <div className="ltr-chips">
          {chips.map(c => <span key={c} className="ltr-chip">{c}</span>)}
          {pick(ride, 'tripStatus') && <span className="ltr-chip ltr-chip-on">{pick(ride, 'tripStatus')}</span>}
        </div>
        <span className="ltr-range ltr-mono">
          {data ? `${data.date0.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} · ${hms(data.t0)} – ${hms(data.t0 + data.end)}` : ''}
        </span>
        {onClose && (
          <button className="ltr-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
          </button>
        )}
      </header>

      <div className="ltr-body">
        <div ref={hostRef} className="ltr-host" />

        {(loading || error || (!loading && !data)) && (
          <div className="ltr-state">
            {loading ? 'Loading replay…' : error || 'No GPS records for this ride.'}
          </div>
        )}

        <TripReplayInfo ride={ride} data={data} timeline={timeline} wide={wide} onJump={jumpTo} />

        <div className="ltr-right">
          {wide && data && (
            <div className="ltr-card ltr-readout">
              <div className="ltr-between"><span className="ltr-lbl">At</span><span className="ltr-mono">{clock}</span></div>
              <div className="ltr-speedrow">
                <span className="ltr-mono ltr-speed">{Math.round(cur.speed || 0)}</span>
                <span className="ltr-sub">km/h</span>
                <span className="ltr-status" style={{ color: statusColor }}><i style={{ background: statusColor }} />{statusLabel}</span>
              </div>
              {cur.fix && cur.fix.over && <span className="ltr-over">Over speed</span>}
              {cur.fix && cur.fix.address && <div className="ltr-addr">{cur.fix.address}</div>}
              <div className="ltr-pair">
                <div><div className="ltr-lbl">Covered</div><div className="ltr-mono">{s ? `${(s.hsum ? cur.covered / 1000 / s.hsum * s.km : 0).toFixed(1)} / ${s.km.toFixed(1)} km` : '—'}</div></div>
                <div><div className="ltr-lbl">Heading</div><div className="ltr-mono">{COMPASS[Math.round(heading / 45) % 8]} · {heading}°</div></div>
              </div>
            </div>
          )}
          {data && data.manualHint && (
            <div className="ltr-card ltr-hint" role="note">
              <div className="ltr-hint-title">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 20h20L12 3z" /><path d="M12 10v4M12 17h.01" /></svg>
                Trip not fully driven by driver
              </div>
              <p>
                Assigned, boarded and completed within {data.manualHint.mins} min
                {data.manualHint.from && data.manualHint.to ? ` (${data.manualHint.from}–${data.manualHint.to})` : ''}.
                This trip may have been completed manually by a system operator.
              </p>
            </div>
          )}
        </div>

        <div className="ltr-zoom">
          <button onClick={() => mapRef.current && mapRef.current.zoomIn()} aria-label="Zoom in">+</button>
          <button onClick={() => mapRef.current && mapRef.current.zoomOut()} aria-label="Zoom out">−</button>
          <button onClick={fitAll} aria-label="Fit route" title="Fit route">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>
          </button>
        </div>

        <div className="ltr-bar">
          <div className="ltr-barrow">
            <div className="ltr-row">
              <button className="ltr-round" onClick={() => seek(0)} aria-label="Restart" title="Restart">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor"><path d="M6 5h2v14H6zM20 5v14L9 12z" /></svg>
              </button>
              <button className="ltr-play" onClick={toggle} disabled={!data} aria-label="Play or pause" title="Play / pause (Space) · ← → seek 30 s">
                {playing
                  ? <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
                  : <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M7 4.5v15L20 12z" /></svg>}
              </button>
            </div>
            <span className="ltr-mono ltr-clock">{clock}</span>
            <div ref={trackRef} className="ltr-track" onPointerDown={onScrubDown}>
              <div className="ltr-rail" style={{ background: data ? data.gradient : 'rgba(255,255,255,.12)' }}>
                <div className="ltr-mask" style={{ left: pct }} />
              </div>
              {stopTicks}
              <div className="ltr-thumb" style={{ left: pct }} />
            </div>
            <span className="ltr-mono ltr-clock ltr-dim">{data ? hms(data.t0 + data.end) : '--:--:--'}</span>
            <div className="ltr-seg">
              {RATES.map(r => (
                <button key={r} className="ltr-mono" aria-pressed={rate === r} onClick={() => setRate(r)}>×{r}</button>
              ))}
            </div>
            <button className="ltr-follow" aria-pressed={!!prefs.soundOn} onClick={toggleSound} aria-label="Sound" title="Sound (engine + event chimes)">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
                <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
                {prefs.soundOn ? <path d="M16 9a4 4 0 0 1 0 6M18.5 6.5a8 8 0 0 1 0 11" /> : <path d="M17 9l5 6M22 9l-5 6" />}
              </svg>
            </button>
            <button className="ltr-follow" aria-pressed={follow} onClick={() => {
              const next = !follow;
              setFollow(next);
              if (next && layers.current.vehicle && mapRef.current) mapRef.current.panTo(layers.current.vehicle.getLatLng());
            }}>Follow</button>
          </div>
          <div className="ltr-legendrow">
            {legend.map(g => <span key={g.label} className="ltr-legend"><i style={{ background: g.color }} />{g.label}</span>)}
            <div className="ltr-display">
              <div className="ltr-row"><span>Vehicle</span>
                <div className="ltr-seg">
                  {[['car', 'Car'], ['puck', 'Puck'], ['plate', 'Plate']].map(([k, lbl]) => (
                    <button key={k} aria-pressed={prefs.markerStyle === k} onClick={() => updatePrefs({ markerStyle: k })}>{lbl}</button>
                  ))}
                </div>
              </div>
              <div className="ltr-row"><span>Trail</span>
                <div className="ltr-seg">
                  {[['speed', 'Speed'], ['solid', 'Solid']].map(([k, lbl]) => (
                    <button key={k} aria-pressed={prefs.trailMode === k} onClick={() => updatePrefs({ trailMode: k })}>{lbl}</button>
                  ))}
                </div>
              </div>
              <button className="ltr-switch" aria-pressed={prefs.showStops} onClick={() => updatePrefs({ showStops: !prefs.showStops })}>
                Stops<span><i /></span>
              </button>
              <button className="ltr-switch" aria-pressed={showStatus} title="Show trip status and time on the map" onClick={toggleStatus}>
                Status<span><i /></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
